/**
 * Search Context
 * Manages universal search state across the application
 */

import React, { createContext, useContext, useState, useCallback } from 'react'

interface SearchContextType {
  searchQuery: string
  searchContext: string
  setSearchQuery: (query: string) => void
  setSearchContext: (context: string) => void
  clearSearch: () => void
}

const SearchContext = createContext<SearchContextType | undefined>(undefined)

export const useSearch = () => {
  const context = useContext(SearchContext)
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider')
  }
  return context
}

interface SearchProviderProps {
  children: React.ReactNode
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQueryState] = useState('')
  const [searchContext, setSearchContextState] = useState('/')

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query)
  }, [])

  const setSearchContext = useCallback((context: string) => {
    setSearchContextState(context)
  }, [])

  const clearSearch = useCallback(() => {
    setSearchQueryState('')
  }, [])

  const value = {
    searchQuery,
    searchContext,
    setSearchQuery,
    setSearchContext,
    clearSearch
  }

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  )
}

export default SearchContext